import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Play, Square, RefreshCw, Database, Mountain, Zap,
  TriangleAlert, MapPin, Pickaxe, Waves, Search, X, ChevronLeft, ChevronRight, Map,
} from "lucide-react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import type { LatLngBounds } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { authFetch } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { AnmDueDiligenceTab } from "@/components/anm-enrichment-tabs";
import ModuleHeader from "@/components/ModuleHeader";

const FASE_COLORS: Record<string, string> = {
  "CONCESSÃO DE LAVRA": "destructive",
  "REQUERIMENTO DE LAVRA": "destructive",
  "AUTORIZAÇÃO DE PESQUISA": "secondary",
  "REQUERIMENTO DE PESQUISA": "outline",
  "RECONHECIMENTO GEOLÓGICO": "outline",
  "DIREITO DE REQUERER A LAVRA": "secondary",
  "REQUERIMENTO DE LAVRA GARIMPEIRA": "destructive",
  "LICENCIAMENTO": "secondary",
  "REGISTRO DE EXTRAÇÃO": "secondary",
  "DISPONIBILIDADE": "outline",
};

const RISCO_COLORS: Record<string, string> = {
  "Alto": "destructive",
  "Médio": "secondary",
  "Baixo": "outline",
};

function faseBadge(fase: string) {
  const variant = (FASE_COLORS[fase] || "outline") as any;
  return <Badge variant={variant} className="text-xs">{fase}</Badge>;
}

function riscoBadge(risco: string) {
  const variant = (RISCO_COLORS[risco] || "outline") as any;
  return <Badge variant={variant} className="text-xs">{risco}</Badge>;
}

function MapFitter({ bounds }: { bounds: L.LatLngBounds }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      map.fitBounds(bounds, { padding: [30, 30] });
    }, 80);
    return () => clearTimeout(t);
  }, [map, bounds]);
  return null;
}

function AnmProcessoMap({ geometry }: { geometry: any }) {
  const bounds = useMemo((): L.LatLngBounds | null => {
    if (!geometry) return null;
    try {
      const layer = L.geoJSON(geometry);
      const b = layer.getBounds();
      return b.isValid() ? b : null;
    } catch {
      return null;
    }
  }, [geometry]);

  if (!geometry || !bounds) {
    return (
      <div className="flex items-center justify-center h-[300px] bg-muted/50 rounded-lg border text-muted-foreground text-sm">
        <MapPin className="h-5 w-5 mr-2 opacity-50" />
        Este processo não possui geometria cadastrada
      </div>
    );
  }

  return (
    <div className="h-[300px] rounded-lg overflow-hidden border">
      <MapContainer
        bounds={bounds}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
        attributionControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <GeoJSON
          key={JSON.stringify(bounds.toBBoxString())}
          data={geometry}
          style={() => ({
            color: "#d97706",
            weight: 2,
            fillColor: "#f59e0b",
            fillOpacity: 0.25,
          })}
        />
        <MapFitter bounds={bounds} />
      </MapContainer>
    </div>
  );
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function phaseName(phase: string) {
  const names: Record<string, string> = {
    idle: "Aguardando",
    import_processos: "Importando processos",
    import_barragens: "Importando barragens",
    enriching: "Enriquecendo SICAR",
    completed: "Concluído",
    error: "Erro",
    stopped: "Parado",
  };
  return names[phase] || phase;
}

function phaseVariant(phase: string): "default" | "secondary" | "destructive" | "outline" {
  if (phase === "completed") return "default";
  if (phase === "error") return "destructive";
  if (phase === "stopped") return "secondary";
  if (phase === "idle") return "outline";
  return "secondary";
}

export default function AnmPage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [phases, setPhases] = useState<string[]>(["import", "barragens", "enrich"]);
  const [tab, setTab] = useState("worker");

  const [filterUf, setFilterUf] = useState("");
  const [filterFase, setFilterFase] = useState("");
  const [filterSubs, setFilterSubs] = useState("");
  const [filterUso, setFilterUso] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterAreaMin, setFilterAreaMin] = useState("");
  const [filterAreaMax, setFilterAreaMax] = useState("");
  const [listPage, setListPage] = useState(0);
  const [selectedProcesso, setSelectedProcesso] = useState<string | null>(null);
  const [dialogTab, setDialogTab] = useState("detalhes");
  const PAGE_SIZE = 20;

  const debouncedSearch = useDebounce(filterSearch, 500);
  const debouncedAreaMin = useDebounce(filterAreaMin, 500);
  const debouncedAreaMax = useDebounce(filterAreaMax, 500);

  const togglePhase = (p: string) => {
    setPhases((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  const statusQ = useQuery({
    queryKey: ["/api/anm/import/status"],
    queryFn: () => authFetch("/api/anm/import/status"),
    refetchInterval: 3000,
  });

  const fasesQ = useQuery({
    queryKey: ["/api/anm/stats/fases"],
    queryFn: () => authFetch("/api/anm/stats/fases"),
    enabled: tab === "processos",
  });

  const ufsQ = useQuery({
    queryKey: ["/api/anm/stats/ufs"],
    queryFn: () => authFetch("/api/anm/stats/ufs"),
    enabled: tab === "processos",
  });

  const subsQ = useQuery({
    queryKey: ["/api/anm/stats/substancias"],
    queryFn: () => authFetch("/api/anm/stats/substancias"),
    enabled: tab === "substancias",
  });

  const barragensRiscoQ = useQuery({
    queryKey: ["/api/anm/stats/barragens-risco"],
    queryFn: () => authFetch("/api/anm/stats/barragens-risco"),
    enabled: tab === "barragens",
  });

  const enrichUfQ = useQuery({
    queryKey: ["/api/anm/stats/enrich-by-uf"],
    queryFn: () => authFetch("/api/anm/stats/enrich-by-uf"),
    enabled: tab === "enrich",
  });

  const filterOptionsQ = useQuery({
    queryKey: ["/api/anm/processos/filter-options"],
    queryFn: () => authFetch("/api/anm/processos/filter-options"),
    enabled: tab === "lista",
    staleTime: 5 * 60 * 1000,
  });

  const listQueryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (filterUf) p.set("uf", filterUf);
    if (filterFase) p.set("fase", filterFase);
    if (filterSubs) p.set("subs", filterSubs);
    if (filterUso) p.set("uso", filterUso);
    if (debouncedSearch) p.set("search", debouncedSearch);
    if (debouncedAreaMin) p.set("area_min", debouncedAreaMin);
    if (debouncedAreaMax) p.set("area_max", debouncedAreaMax);
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(listPage * PAGE_SIZE));
    p.set("sort", "area_ha");
    p.set("order", "desc");
    return p.toString();
  }, [filterUf, filterFase, filterSubs, filterUso, debouncedSearch, debouncedAreaMin, debouncedAreaMax, listPage]);

  const processosListQ = useQuery({
    queryKey: ["/api/anm/processos", listQueryParams],
    queryFn: () => authFetch(`/api/anm/processos?${listQueryParams}`),
    enabled: tab === "lista",
    placeholderData: (prev: any) => prev,
  });

  const processoDetalheQ = useQuery({
    queryKey: ["/api/anm/processos", selectedProcesso],
    queryFn: () => authFetch(`/api/anm/processos/${encodeURIComponent(selectedProcesso!)}`),
    enabled: !!selectedProcesso,
  });

  useEffect(() => { setListPage(0); }, [filterUf, filterFase, filterSubs, filterUso, debouncedSearch, debouncedAreaMin, debouncedAreaMax]);

  const clearFilters = () => {
    setFilterUf(""); setFilterFase(""); setFilterSubs("");
    setFilterUso(""); setFilterSearch(""); setFilterAreaMin(""); setFilterAreaMax("");
    setListPage(0);
  };

  const hasActiveFilters = !!(filterUf || filterFase || filterSubs || filterUso || filterSearch || filterAreaMin || filterAreaMax);

  const startMut = useMutation({
    mutationFn: () =>
      authFetch("/api/anm/import/start", {
        method: "POST",
        body: JSON.stringify({ phases }),
      }),
    onSuccess: () => {
      toast({ title: "ANM Worker iniciado", description: `Fases: ${phases.join(", ")}` });
      qc.invalidateQueries({ queryKey: ["/api/anm/import/status"] });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const stopMut = useMutation({
    mutationFn: () =>
      authFetch("/api/anm/import/stop", { method: "POST" }),
    onSuccess: () => {
      toast({ title: "Sinal de parada enviado" });
    },
  });

  const resetMut = useMutation({
    mutationFn: () =>
      authFetch("/api/anm/enrich/reset", { method: "POST" }),
    onSuccess: () => {
      toast({ title: "Enriquecimento resetado" });
      qc.invalidateQueries({ queryKey: ["/api/anm/import/status"] });
      qc.invalidateQueries({ queryKey: ["/api/anm/stats/enrich-by-uf"] });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const status = statusQ.data as any;
  const worker = status?.worker;
  const db = status?.database;

  const isRunning =
    worker?.phase === "import_processos" ||
    worker?.phase === "import_barragens" ||
    worker?.phase === "enriching";

  const UF_LIST = [
    "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA",
    "MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN",
    "RO","RR","RS","SC","SE","SP","TO",
  ];

  const ufProgress: Record<string, { status: string; processos: number; outros_layers: number }> =
    worker?.uf_progress || {};
  const ufsDone: string[] = worker?.ufs_done || [];

  const importProgressPct = worker?.phase === "import_processos"
    ? Math.round((ufsDone.length / UF_LIST.length) * 100)
    : 0;

  const enrichProgressPct = worker?.phase === "enriching" && worker?.total_to_enrich > 0
    ? Math.round((worker.enriched / worker.total_to_enrich) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <ModuleHeader
        icon={Pickaxe}
        iconColor="text-amber-600"
        title="ANM SIGMINE"
        description="Processos minerários, barragens de rejeitos e enriquecimento geoespacial"
        recordCount={db?.anm_processos}
        recordLabel="processos"
        onRefresh={() => qc.invalidateQueries({ queryKey: ["/api/anm/import/status"] })}
        refreshing={statusQ.isFetching}
        isLoading={statusQ.isLoading}
        docsHref="/docs"
      />

      <div className="grid grid-cols-2 gap-4">
        {[
          {
            label: "Processos ANM",
            value: db?.anm_processos ?? 0,
            icon: <Pickaxe className="h-4 w-4 text-amber-600" />,
            color: "text-amber-700",
          },
          {
            label: "Barragens",
            value: db?.anm_barragens ?? 0,
            icon: <Waves className="h-4 w-4 text-blue-600" />,
            color: "text-blue-700",
          },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-1">
                {s.icon}
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <div className={`text-2xl font-bold ${s.color}`}>
                {statusQ.isLoading ? <Skeleton className="h-7 w-20" /> : (s.value ?? 0).toLocaleString("pt-BR")}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="worker" data-testid="tab-worker">Worker</TabsTrigger>
          <TabsTrigger value="lista" data-testid="tab-lista">Lista</TabsTrigger>
          <TabsTrigger value="processos" data-testid="tab-processos">Processos</TabsTrigger>
          <TabsTrigger value="substancias" data-testid="tab-substancias">Substâncias</TabsTrigger>
          <TabsTrigger value="barragens" data-testid="tab-barragens">Barragens</TabsTrigger>
          <TabsTrigger value="enrich" data-testid="tab-enrich">Enriquecimento</TabsTrigger>
          <TabsTrigger value="tah" data-testid="tab-tah">TAH</TabsTrigger>
        </TabsList>

        <TabsContent value="worker" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Status do Worker</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {statusQ.isLoading ? (
                  <Skeleton className="h-20 w-full" />
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={phaseVariant(worker?.phase || "idle")}>
                        {phaseName(worker?.phase || "idle")}
                      </Badge>
                      {worker?.uf_atual && (
                        <span className="text-sm text-muted-foreground">
                          Processando: <strong className="text-amber-600">{worker.uf_atual}</strong>
                        </span>
                      )}
                    </div>

                    {worker?.message && (
                      <p className="text-sm text-muted-foreground">{worker.message}</p>
                    )}

                    {worker?.phase === "import_processos" && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Estados concluídos</span>
                          <span className="font-medium">{ufsDone.length} / {UF_LIST.length}</span>
                        </div>
                        <Progress value={importProgressPct} className="h-2" />
                      </div>
                    )}

                    {worker?.phase === "enriching" && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Enriquecimento SICAR</span>
                          <span className="font-medium">{enrichProgressPct}%</span>
                        </div>
                        <Progress value={enrichProgressPct} className="h-2" />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {[
                        ["Processos importados", worker?.processos_importados],
                        ["Barragens importadas", worker?.barragens_importadas],
                        ["Enriquecidos", worker?.enriched],
                        ["Erros", worker?.errors],
                      ].map(([label, val]) => (
                        <div key={label as string}>
                          <span className="text-muted-foreground">{label}</span>
                          <div className="font-semibold">{(val as number)?.toLocaleString("pt-BR") ?? "—"}</div>
                        </div>
                      ))}
                    </div>

                    {worker?.startedAt && (
                      <p className="text-xs text-muted-foreground">
                        Iniciado: {new Date(worker.startedAt).toLocaleString("pt-BR")}
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Controles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Fases a executar</Label>
                  {[
                    { id: "import", label: "Importar Processos Minerários (SIGMINE)", icon: <Pickaxe className="h-3 w-3" /> },
                    { id: "barragens", label: "Importar Barragens (SIGBM)", icon: <Waves className="h-3 w-3" /> },
                    { id: "enrich", label: "Enriquecer registros SICAR", icon: <Database className="h-3 w-3" /> },
                  ].map((p) => (
                    <div key={p.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`phase-${p.id}`}
                        checked={phases.includes(p.id)}
                        onCheckedChange={() => togglePhase(p.id)}
                        disabled={isRunning}
                        data-testid={`checkbox-phase-${p.id}`}
                      />
                      <label
                        htmlFor={`phase-${p.id}`}
                        className="text-sm flex items-center gap-1.5 cursor-pointer"
                      >
                        {p.icon}
                        {p.label}
                      </label>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => startMut.mutate()}
                    disabled={isRunning || phases.length === 0 || startMut.isPending}
                    className="flex-1"
                    data-testid="button-start-worker"
                  >
                    <Play className="h-4 w-4 mr-1" />
                    Iniciar
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => stopMut.mutate()}
                    disabled={!isRunning || stopMut.isPending}
                    data-testid="button-stop-worker"
                  >
                    <Square className="h-4 w-4 mr-1" />
                    Parar
                  </Button>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => resetMut.mutate()}
                  disabled={isRunning || resetMut.isPending}
                  data-testid="button-reset-enrich"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Resetar Enriquecimento
                </Button>

                <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-300 space-y-1">
                  <p className="font-semibold flex items-center gap-1">
                    <TriangleAlert className="h-3 w-3" />
                    Sobre a importação
                  </p>
                  <p>A importação consulta a API ArcGIS do ANM por UF e fase. Pode levar 30–90 minutos para o Brasil completo.</p>
                  <p>O download de barragens é via CSV direto do servidor ANM.</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {(isRunning && worker?.phase === "import_processos") || Object.keys(ufProgress).length > 0 ? (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-amber-600" />
                    Progresso por Estado — Importação SIGMINE
                  </CardTitle>
                  <span className="text-sm text-muted-foreground font-medium">
                    {ufsDone.length}/{UF_LIST.length} estados
                  </span>
                </div>
                {worker?.phase === "import_processos" && (
                  <Progress value={importProgressPct} className="h-2 mt-2" />
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-9 gap-1.5">
                  {UF_LIST.map((uf) => {
                    const p = ufProgress[uf];
                    const isCurrent = worker?.uf_atual === uf;
                    const isDone = p?.status === "done" || ufsDone.includes(uf);
                    const isError = p?.status === "error";
                    const isRunningUf = p?.status === "running" || isCurrent;
                    const count = (p?.processos || 0) + (p?.outros_layers || 0);

                    let cellClass = "rounded px-1.5 py-1.5 text-center text-[10px] font-semibold border transition-all ";
                    if (isRunningUf) {
                      cellClass += "bg-amber-100 dark:bg-amber-900/40 border-amber-400 text-amber-800 dark:text-amber-300 animate-pulse";
                    } else if (isDone) {
                      cellClass += "bg-green-100 dark:bg-green-900/30 border-green-400 text-green-800 dark:text-green-300";
                    } else if (isError) {
                      cellClass += "bg-red-100 dark:bg-red-900/30 border-red-400 text-red-700 dark:text-red-300";
                    } else {
                      cellClass += "bg-muted border-border text-muted-foreground";
                    }

                    return (
                      <div key={uf} className={cellClass} title={isDone ? `${count.toLocaleString("pt-BR")} registros` : isRunningUf ? "Importando..." : "Aguardando"}>
                        <div>{uf}</div>
                        {isDone && count > 0 && (
                          <div className="text-[9px] font-normal opacity-80">{count >= 1000 ? `${(count / 1000).toFixed(1)}k` : count}</div>
                        )}
                        {isRunningUf && <div className="text-[9px]">...</div>}
                        {isError && <div className="text-[9px]">err</div>}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 dark:bg-green-900/50 border border-green-400 inline-block" /> Concluído</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200 dark:bg-amber-900/50 border border-amber-400 inline-block" /> Importando</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-muted border border-border inline-block" /> Pendente</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 dark:bg-red-900/50 border border-red-400 inline-block" /> Erro</span>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {db?.anm_outros_layers && db.anm_outros_layers.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Outros Layers (Proteção, Arrendamentos, Bloqueios, Reservas)</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Layer</TableHead>
                      <TableHead className="text-right">Registros</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {db.anm_outros_layers.map((l: any) => (
                      <TableRow key={l.layer_tipo}>
                        <TableCell className="capitalize">{l.layer_tipo?.replace(/_/g, " ")}</TableCell>
                        <TableCell className="text-right font-mono">{parseInt(l.count).toLocaleString("pt-BR")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="lista" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Search className="h-4 w-4" /> Filtros
                </CardTitle>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="button-clear-filters">
                    <X className="h-3 w-3 mr-1" /> Limpar
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">UF</Label>
                  <Select value={filterUf} onValueChange={(v) => setFilterUf(v === "__all__" ? "" : v)}>
                    <SelectTrigger data-testid="select-filter-uf"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {(filterOptionsQ.data?.ufs || []).map((u: string) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fase</Label>
                  <Select value={filterFase} onValueChange={(v) => setFilterFase(v === "__all__" ? "" : v)}>
                    <SelectTrigger data-testid="select-filter-fase"><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todas</SelectItem>
                      {(filterOptionsQ.data?.fases || []).map((f: string) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Substância</Label>
                  <Select value={filterSubs} onValueChange={(v) => setFilterSubs(v === "__all__" ? "" : v)}>
                    <SelectTrigger data-testid="select-filter-subs"><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todas</SelectItem>
                      {(filterOptionsQ.data?.substancias || []).map((s: string) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Uso</Label>
                  <Select value={filterUso} onValueChange={(v) => setFilterUso(v === "__all__" ? "" : v)}>
                    <SelectTrigger data-testid="select-filter-uso"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {(filterOptionsQ.data?.usos || []).map((u: string) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Busca (processo, nome, substância)</Label>
                  <Input
                    placeholder="Pesquisar..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    data-testid="input-filter-search"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Área mín (ha)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filterAreaMin}
                    onChange={(e) => setFilterAreaMin(e.target.value)}
                    data-testid="input-filter-area-min"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Área máx (ha)</Label>
                  <Input
                    type="number"
                    placeholder="∞"
                    value={filterAreaMax}
                    onChange={(e) => setFilterAreaMax(e.target.value)}
                    data-testid="input-filter-area-max"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              {processosListQ.isLoading ? (
                <div className="space-y-2">
                  {Array(8).fill(0).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-muted-foreground">
                      {(processosListQ.data?.total ?? 0).toLocaleString("pt-BR")} processos encontrados
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={listPage === 0}
                        onClick={() => setListPage((p) => Math.max(0, p - 1))}
                        data-testid="button-prev-page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground min-w-[80px] text-center">
                        {processosListQ.data?.total
                          ? `${listPage + 1} / ${Math.ceil((processosListQ.data?.total || 1) / PAGE_SIZE)}`
                          : "—"}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!processosListQ.data?.total || (listPage + 1) * PAGE_SIZE >= processosListQ.data.total}
                        onClick={() => setListPage((p) => p + 1)}
                        data-testid="button-next-page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Processo</TableHead>
                          <TableHead>Fase</TableHead>
                          <TableHead>Substância</TableHead>
                          <TableHead>UF</TableHead>
                          <TableHead className="text-right">Área (ha)</TableHead>
                          <TableHead>Titular</TableHead>
                          <TableHead className="text-center w-10">Geo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(processosListQ.data?.rows || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                              Nenhum processo encontrado
                            </TableCell>
                          </TableRow>
                        ) : (
                          (processosListQ.data?.rows || []).map((row: any) => (
                            <TableRow
                              key={row.id}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => setSelectedProcesso(row.processo)}
                              data-testid={`row-processo-${row.id}`}
                            >
                              <TableCell className="font-mono text-xs">{row.processo}</TableCell>
                              <TableCell>{faseBadge(row.fase || "—")}</TableCell>
                              <TableCell className="text-xs max-w-[160px] truncate">{row.subs || "—"}</TableCell>
                              <TableCell><Badge variant="outline" className="text-xs">{row.uf}</Badge></TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {row.area_ha ? parseFloat(row.area_ha).toLocaleString("pt-BR", { maximumFractionDigits: 2 }) : "—"}
                              </TableCell>
                              <TableCell className="text-xs max-w-[180px] truncate">{row.nome || "—"}</TableCell>
                              <TableCell className="text-center">
                                <MapPin className={`h-3.5 w-3.5 inline-block ${row.tem_geometria ? "text-green-500" : "text-muted-foreground/30"}`} />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Dialog open={!!selectedProcesso} onOpenChange={(open) => { if (!open) { setSelectedProcesso(null); setDialogTab("detalhes"); } }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Pickaxe className="h-5 w-5 text-amber-600" />
                  Processo {processoDetalheQ.data?.processo || selectedProcesso}
                </DialogTitle>
                <DialogDescription>
                  {processoDetalheQ.data?.ds_processo || "Carregando detalhes..."}
                </DialogDescription>
              </DialogHeader>

              <Tabs value={dialogTab} onValueChange={setDialogTab}>
                <TabsList className="w-full">
                  <TabsTrigger value="detalhes" className="flex-1" data-testid="dialog-tab-detalhes">Detalhes</TabsTrigger>
                  <TabsTrigger value="due-diligence" className="flex-1" data-testid="dialog-tab-due-diligence">Due Diligência</TabsTrigger>
                </TabsList>

                <TabsContent value="detalhes" className="mt-4">
                  {processoDetalheQ.isLoading ? (
                    <div className="space-y-3">
                      {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}
                      <Skeleton className="h-[300px] w-full" />
                    </div>
                  ) : processoDetalheQ.data ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                        {[
                          ["Processo", processoDetalheQ.data.processo],
                          ["Descrição", processoDetalheQ.data.ds_processo],
                          ["Número", processoDetalheQ.data.numero],
                          ["Ano", processoDetalheQ.data.ano],
                          ["Fase", null],
                          ["Substância", processoDetalheQ.data.subs],
                          ["Uso", processoDetalheQ.data.uso],
                          ["Titular", processoDetalheQ.data.nome],
                          ["Área (ha)", processoDetalheQ.data.area_ha ? parseFloat(processoDetalheQ.data.area_ha).toLocaleString("pt-BR", { maximumFractionDigits: 2 }) : "—"],
                          ["UF", processoDetalheQ.data.uf],
                          ["Último Evento", null],
                          ["ID ANM", processoDetalheQ.data.id_anm],
                        ].map(([label, val]) => (
                          <div key={label as string}>
                            <span className="text-muted-foreground text-xs block">{label}</span>
                            {label === "Fase" ? (
                              faseBadge(processoDetalheQ.data.fase || "—")
                            ) : label === "Último Evento" ? (
                              <span className="font-medium text-xs leading-tight block">{processoDetalheQ.data.ult_evento || "—"}</span>
                            ) : (
                              <span className="font-medium">{val || "—"}</span>
                            )}
                          </div>
                        ))}
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Map className="h-4 w-4 text-amber-600" />
                          <span className="text-sm font-medium">Polígono</span>
                        </div>
                        <AnmProcessoMap geometry={processoDetalheQ.data.geometry} />
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">Erro ao carregar detalhes.</p>
                  )}
                </TabsContent>

                <TabsContent value="due-diligence" className="mt-4">
                  {selectedProcesso && (
                    <AnmDueDiligenceTab
                      processo={selectedProcesso}
                      titular={processoDetalheQ.data?.nome}
                    />
                  )}
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="processos" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="h-4 w-4" /> Por Fase
                </CardTitle>
              </CardHeader>
              <CardContent>
                {fasesQ.isLoading ? (
                  <div className="space-y-2">{Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fase</TableHead>
                        <TableHead className="text-right">Processos</TableHead>
                        <TableHead className="text-right">Área Total (ha)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(fasesQ.data as any[] || []).map((r: any) => (
                        <TableRow key={r.fase}>
                          <TableCell>{faseBadge(r.fase || "—")}</TableCell>
                          <TableCell className="text-right font-mono">{parseInt(r.total).toLocaleString("pt-BR")}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{parseFloat(r.area_total_ha || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Por UF
                </CardTitle>
              </CardHeader>
              <CardContent>
                {ufsQ.isLoading ? (
                  <div className="space-y-2">{Array(10).fill(0).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>UF</TableHead>
                        <TableHead className="text-right">Processos</TableHead>
                        <TableHead className="text-right">Área (ha)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(ufsQ.data as any[] || []).map((r: any) => (
                        <TableRow key={r.uf}>
                          <TableCell><Badge variant="outline">{r.uf}</Badge></TableCell>
                          <TableCell className="text-right font-mono">{parseInt(r.total_processos).toLocaleString("pt-BR")}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{parseFloat(r.area_total_ha || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="substancias" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Top 50 Substâncias Minerais</CardTitle>
            </CardHeader>
            <CardContent>
              {subsQ.isLoading ? (
                <div className="space-y-2">{Array(10).fill(0).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Substância</TableHead>
                      <TableHead className="text-right">Processos</TableHead>
                      <TableHead className="text-right">Área Total (ha)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(subsQ.data as any[] || []).map((r: any, i: number) => (
                      <TableRow key={r.substancia}>
                        <TableCell className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-5">{i + 1}</span>
                          {r.substancia}
                        </TableCell>
                        <TableCell className="text-right font-mono">{parseInt(r.total).toLocaleString("pt-BR")}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{parseFloat(r.area_total_ha || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="barragens" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Waves className="h-4 w-4 text-blue-600" />
                Barragens por Categoria de Risco × DPA
              </CardTitle>
            </CardHeader>
            <CardContent>
              {barragensRiscoQ.isLoading ? (
                <div className="space-y-2">{Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Categoria de Risco</TableHead>
                      <TableHead>Dano Potencial Associado</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(barragensRiscoQ.data as any[] || []).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{riscoBadge(r.categoria_risco || "—")}</TableCell>
                        <TableCell>{riscoBadge(r.dano_potencial_associado || "—")}</TableCell>
                        <TableCell className="text-right font-mono">{parseInt(r.total).toLocaleString("pt-BR")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                Dados do SIGBM — Sistema Integrado de Gestão de Barragens de Mineração (ANM). Raio de busca: 100km do centroide do imóvel.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="enrich" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Enriquecimento por UF</CardTitle>
            </CardHeader>
            <CardContent>
              {enrichUfQ.isLoading ? (
                <div className="space-y-2">{Array(8).fill(0).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>UF</TableHead>
                      <TableHead className="text-right">Concluídos</TableHead>
                      <TableHead className="text-right">Com Mineração</TableHead>
                      <TableHead className="text-right">Com Barragem</TableHead>
                      <TableHead className="text-right">Pendentes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(enrichUfQ.data as any[] || []).map((r: any) => (
                      <TableRow key={r.uf}>
                        <TableCell><Badge variant="outline">{r.uf}</Badge></TableCell>
                        <TableCell className="text-right font-mono text-green-600">{parseInt(r.done || 0).toLocaleString("pt-BR")}</TableCell>
                        <TableCell className="text-right font-mono text-amber-700">{parseInt(r.com_mineracao || 0).toLocaleString("pt-BR")}</TableCell>
                        <TableCell className="text-right font-mono text-blue-600">{parseInt(r.com_barragem || 0).toLocaleString("pt-BR")}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{parseInt(r.pending || 0).toLocaleString("pt-BR")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                "Com Mineração" = imóvel tem ao menos 1 processo minerário ativo em sobreposição. "Com Barragem" = ao menos 1 barragem SIGBM num raio de 100km.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tah" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Pickaxe className="h-4 w-4 text-amber-600" />
                TAH — Títulos e Áreas de Habilitação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4 text-sm text-amber-800 dark:text-amber-300">
                <p className="font-semibold flex items-center gap-1 mb-2">
                  <TriangleAlert className="h-4 w-4" />
                  Módulo em desenvolvimento
                </p>
                <p>
                  O módulo TAH (Títulos e Áreas de Habilitação) permitirá consultar e cruzar
                  dados de titulares de processos minerários com proprietários rurais (SNCR → CCIR → CPF).
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                  <li>Importação de dados TAH da ANM por UF</li>
                  <li>Cruzamento CPF/CNPJ titular × SICAR × SNCR</li>
                  <li>Identificação de sobreposição mineração × propriedade rural</li>
                  <li>Mapa de calor de áreas com dupla incidência</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
